import type { RecipeFromTextRequest } from '@recipes/contracts';
import { Alert, Button, Textarea } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ConstraintsFields,
  EMPTY_CONSTRAINTS,
  toAiConstraints,
  type ConstraintsState,
} from '@/features/ai/constraints-fields';
import {
  clearTextDraft,
  loadGenerationRequest,
  loadTextDraft,
  storeGenerationRequest,
  storeTextDraft,
} from '@/features/ai/generation-storage';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useFeature, useProfile } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';

type Mode = RecipeFromTextRequest['mode'];
const MODES: Mode[] = ['auto', 'keywords', 'notes'];

export function TextGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const { data: profile } = useProfile();

  const [text, setText] = useState('');
  const [mode, setMode] = useState<Mode>('auto');
  const [constraints, setConstraints] = useState<ConstraintsState>(EMPTY_CONSTRAINTS);
  const [prefilledDietary, setPrefilledDietary] = useState(false);

  // Restore: a failed generation (?restore=<id>), an unsent draft, or ?text= from /add/ask.
  useEffect(() => {
    const restoreId = params.get('restore');
    const stored = restoreId ? loadGenerationRequest(restoreId) : null;
    const source = stored?.kind === 'text' ? stored.body : (loadTextDraft() ?? null);
    const paramText = params.get('text');
    if (source) {
      setText(source.text ?? '');
      setMode(source.mode ?? 'auto');
      const c = source.constraints;
      if (c) {
        setConstraints({
          servings: c.servings ? String(c.servings) : '',
          maxTotalMinutes: c.maxTotalMinutes ? String(c.maxTotalMinutes) : '',
          cuisine: c.cuisine ?? '',
          variants: (c.variants ?? 1) as 1 | 2 | 3,
          dietaryPreferences: c.dietaryPreferences ?? [],
          equipment: (c.equipment ?? []).join(', '),
        });
        setPrefilledDietary(true);
      }
    }
    if (paramText) setText(paramText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profile && !prefilledDietary) {
      setConstraints((c) => ({ ...c, dietaryPreferences: profile.dietaryPreferences }));
      setPrefilledDietary(true);
    }
  }, [profile, prefilledDietary]);

  const buildBody = (): RecipeFromTextRequest => ({
    text: text.trim(),
    mode,
    constraints: toAiConstraints(constraints, currentLocale()),
  });

  useEffect(() => {
    if (text.trim())
      storeTextDraft({ text, mode, constraints: toAiConstraints(constraints, currentLocale()) });
  }, [text, mode, constraints]);

  const mutation = useMutation({
    mutationFn: (body: RecipeFromTextRequest) => api.ai.recipeFromText(body, crypto.randomUUID()),
    onSuccess: (generation, body) => {
      storeGenerationRequest(generation.id, { kind: 'text', body });
      clearTextDraft();
      navigate(`/add/ai/${generation.id}`);
    },
  });

  const tooShort = text.trim().length < 3;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('ai.text.title')}
        description={t('ai.text.description')}
        backTo="/add"
      />
      {!aiEnabled && <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>}
      {aiEnabled && !consented && <AiConsentNotice />}
      <form
        noValidate
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!tooShort) mutation.mutate(buildBody());
        }}
      >
        <div role="radiogroup" aria-label={t('ai.text.mode')} className="flex flex-wrap gap-2">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              className={`min-h-[40px] rounded-full border px-4 text-sm ${mode === m ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent'}`}
            >
              {t(`ai.text.modes.${m}`)}
            </button>
          ))}
        </div>
        <FormField label={t('ai.text.field')} required hint={t(`ai.text.hints.${mode}`)}>
          <Textarea
            rows={8}
            value={text}
            maxLength={20000}
            data-testid="ai-text-input"
            placeholder={t('ai.text.placeholder')}
            onChange={(e) => setText(e.target.value)}
          />
        </FormField>
        <ConstraintsFields value={constraints} onChange={setConstraints} />
        {mutation.error && (
          <Alert variant="destructive" title={t('ai.failed')}>
            <p>{describeError(mutation.error)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('ai.inputKept')}</p>
          </Alert>
        )}
        <p className="text-xs text-muted-foreground">{t('ai.text.disclaimer')}</p>
        <Button
          type="submit"
          size="lg"
          loading={mutation.isPending}
          disabled={!aiEnabled || !consented || tooShort}
          data-testid="ai-text-submit"
        >
          {t('ai.text.submit')}
        </Button>
      </form>
    </PageContainer>
  );
}
